import { useSnackbar } from "@/composables/useSnackbar";
import { loginRequest, fetchMe } from "@/services/authServices";
import http from "@/services/http";
import { exchangeGoogleCode } from "@/services/authServices";

// SALVIA-744: o token Bearer não existe mais no front — a credencial é o
// cookie de sessão httpOnly, que o JavaScript não lê. Apaga a cópia que um
// login anterior à migração tenha deixado: ela continuaria legível por
// qualquer script até expirar.
try {
  localStorage.removeItem("access_token");
} catch {
  // storage indisponível (aba privada): não havia o que apagar
}

/**
 * O que do usuário pode ficar gravado no navegador.
 *
 * O cache existe só para a primeira tela não piscar e para o router saber o
 * papel antes do /api/user responder. Para isso bastam estes campos.
 *
 * Antes gravava o objeto INTEIRO — CPF, endereço, telefones, e-mail, data de
 * nascimento. O localStorage é legível por qualquer script da página e não
 * expira ao fechar a aba: o token saiu dele (SALVIA-744), mas o dado pessoal
 * continuava lá. O usuário completo agora vive só na memória da aba.
 */
const CAMPOS_EM_CACHE = ["id", "name", "role_id", "user_type"];

function resumoDoUsuario(user) {
  if (!user) return null;
  return Object.fromEntries(
    CAMPOS_EM_CACHE.filter((c) => c in user).map((c) => [c, user[c]]),
  );
}

/**
 * Lê o cache e, se ele vier no formato antigo (com dado pessoal), regrava
 * reduzido na hora — limpa os navegadores que já tinham o objeto completo.
 */
function lerUsuarioEmCache() {
  try {
    const salvo = JSON.parse(localStorage.getItem("user"));
    if (!salvo) return null;
    const resumo = resumoDoUsuario(salvo);
    if (Object.keys(salvo).length !== Object.keys(resumo).length) {
      localStorage.setItem("user", JSON.stringify(resumo));
    }
    return resumo;
  } catch {
    return null;
  }
}

const state = () => ({
  // Cache do usuário para a primeira tela não piscar. NÃO é credencial: quem
  // decide se a pessoa está logada é o backend, pela sessão, no ensureAuth.
  user: lerUsuarioEmCache(),
  userType: localStorage.getItem("user_type") || null,

  // blob URL do avatar (vem do endpoint /api/user/avatar)
  profileImageUrl: null,

  // Sempre false ao abrir: o cache é só um resumo, então o usuário completo é
  // buscado UMA vez por carregamento de página (no ensureAuth do router).
  // Antes, cache presente = "já carregado", e a busca nunca acontecia.
  meLoaded: false,
  loading: false,
});

const getters = {
  isAuthenticated: (s) => !!s.user,
  user: (s) => s.user,
  userId: (s) => s.user?.id ?? null,
  roleId: (s) => s.user?.role_id ?? null,
  userType: (s) => s.userType,
  profileImageUrl: (s) => s.profileImageUrl,
  loading: (s) => s.loading,
};

const mutations = {
  SET_LOADING(state, v) {
    state.loading = v;
  },

  SET_USER(state, user) {
    state.user = user; // completo, só em memória
    state.meLoaded = true;
    localStorage.setItem("user", JSON.stringify(resumoDoUsuario(user)));
  },

  SET_USER_TYPE(state, userType) {
    state.userType = userType;
    if (userType) localStorage.setItem("user_type", userType);
    else localStorage.removeItem("user_type");
  },

  SET_PROFILE_IMAGE_URL(state, url) {
    state.profileImageUrl = url;
  },

  RESET_AUTH(state) {
    if (state.profileImageUrl?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(state.profileImageUrl);
      } catch {}
    }

    state.user = null;
    state.userType = null;
    state.profileImageUrl = null;
    state.meLoaded = false;

    localStorage.removeItem("user");
    localStorage.removeItem("user_type");
  },
};

const actions = {
  async login({ commit, dispatch }, { email, password }) {
    commit("SET_LOADING", true);
    try {
      await loginRequest(email, password);

      const user = await dispatch("fetchMe", { force: true });
      return user;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchMe({ state, commit, dispatch }, { force = false, sondagem = false } = {}) {
    if (!force && state.meLoaded && state.user) {
      if (!state.profileImageUrl) {
        await dispatch("fetchProfileImage");
      }
      if (state.user?.id) {
        try {
          dispatch("notifications/subscribe", state.user.id, { root: true });
        } catch (e) {
          console.warn("[auth] notifications/subscribe falhou (ignorado):", e);
        }
      }
      return state.user;
    }

    try {
      const user = await fetchMe({ sondagem }); // GET /api/user
      commit("SET_USER", user);
      commit("SET_USER_TYPE", user.user_type || null);

      await dispatch("fetchProfileImage");

      if (user?.id) {
        try {
          dispatch("notifications/subscribe", user.id, { root: true });
        } catch (e) {
          console.warn("[auth] notifications/subscribe falhou (ignorado):", e);
        }
      }

      return user;
    } catch (err) {
      if (err.response?.status === 401) commit("RESET_AUTH");
      throw err;
    }
  },

  async refreshMe({ dispatch }) {
    return await dispatch("fetchMe", { force: true });
  },

  /**
   * Há sessão? Com o usuário em cache, confia nele (o primeiro 401 de qualquer
   * chamada desfaz o engano). Sem cache, PERGUNTA ao backend — o cookie pode
   * existir mesmo com o localStorage limpo — sem tratar o 401 como expiração.
   *
   * Só 401/419 querem dizer "não está logado". Antes, QUALQUER falha do
   * `/api/user` — servidor reiniciando, banco sendo restaurado, rede caindo —
   * mandava a pessoa para o login com a sessão ainda válida (visto nos testes
   * do cofre em 15/10/2026). Com o usuário em cache, um erro de servidor
   * agora só avisa e deixa a pessoa onde está; o próximo 401 de verdade, se
   * vier, continua levando ao login pelo interceptor do http.
   */
  async ensureAuth({ dispatch, state }) {
    try {
      const user = await dispatch("fetchMe", { sondagem: true });
      return !!user;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 419) return false;
      if (!state.user) return false;

      useSnackbar().warning(
        "Não foi possível falar com o servidor agora. Você continua conectado — tente de novo em instantes.",
        6000,
      );
      return true;
    }
  },

  logout({ commit, dispatch }) {
    try {
      dispatch("notifications/unsubscribe", null, { root: true });
    } catch (e) {
      console.warn("[auth] notifications/unsubscribe falhou (ignorado):", e);
    }
    commit("RESET_AUTH");
  },

  async fetchProfileImage({ state, commit }) {
    if (state.profileImageUrl?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(state.profileImageUrl);
      } catch {}
    }

    try {
      const res = await http.get("/api/user/avatar", {
        responseType: "blob",
        params: { v: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });

      const url = URL.createObjectURL(res.data);
      commit("SET_PROFILE_IMAGE_URL", url);
      return url;
    } catch (err) {
      commit("SET_PROFILE_IMAGE_URL", null);
      return null;
    }
  },

  // ====== GOOGLE LOGIN (novo fluxo) ======
  // exchange abre a sessão (cookie) e retorna { needs_role }
  async loginWithGoogle({ commit, dispatch }, { code }) {
    commit("SET_LOADING", true);
    try {
      const data = await exchangeGoogleCode(code);

      // força carregar user atualizado do backend
      const user = await dispatch("fetchMe", { force: true });

      // needsRole vem do backend, mas garantimos fallback:
      const needsRole = !!data.needs_role || !user?.role_id;

      return { user, needsRole };
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export default { namespaced: true, state, getters, mutations, actions };
